import type { CSSProperties } from 'react';

export type HeatmapMetricId =
  | 'roas'
  | 'acos'
  | 'cpc'
  | 'spend'
  | 'sales'
  | 'units'
  | 'clicks'
  | 'impressions'
  | 'ctr'
  | 'conversion'
  | 'asp';

type MetricRange = { min: number; max: number };

type ThresholdPalette = {
  belowLow: string;
  belowHigh: string;
  aboveLow: string;
  aboveHigh: string;
};

// Two-tone threshold palettes:
// - ROAS: below is red/orange; above is green
// - ACoS: inverse of ROAS — below is green; above is red
// - Sales: below is cool blue/gray; above is teal
// - Other metrics keep a single hue (below/above identical) until we extend the UX further.
const METRIC_HEATMAP_PALETTES: Record<HeatmapMetricId, ThresholdPalette> = {
  roas: {
    belowLow: '#fee2e2',
    belowHigh: '#ef4444',
    aboveLow: '#d1fae5',
    aboveHigh: '#059669',
  },
  acos: {
    belowLow: '#d1fae5',
    belowHigh: '#059669',
    aboveLow: '#fee2e2',
    aboveHigh: '#ef4444',
  },
  spend: {
    belowLow: '#ffedd5',
    belowHigh: '#f59e0b',
    aboveLow: '#ffedd5',
    aboveHigh: '#f59e0b',
  },
  cpc: {
    belowLow: '#ffedd5',
    belowHigh: '#f59e0b',
    aboveLow: '#ffedd5',
    aboveHigh: '#f59e0b',
  },
  sales: {
    // Below threshold: rose/red (different from ROAS red/orange)
    belowLow: '#ffe4e6',
    belowHigh: '#fb7185',
    // Above threshold: lime/green (different from ROAS emerald)
    aboveLow: '#bbf7d0',
    aboveHigh: '#22c55e',
  },
  units: {
    belowLow: '#eef6ff',
    belowHigh: '#5980a6',
    aboveLow: '#eef6ff',
    aboveHigh: '#5980a6',
  },
  clicks: {
    belowLow: '#eef6ff',
    belowHigh: '#5980a6',
    aboveLow: '#eef6ff',
    aboveHigh: '#5980a6',
  },
  impressions: {
    belowLow: '#eef6ff',
    belowHigh: '#416180',
    aboveLow: '#eef6ff',
    aboveHigh: '#416180',
  },
  ctr: {
    belowLow: '#eef6ff',
    belowHigh: '#416180',
    aboveLow: '#eef6ff',
    aboveHigh: '#416180',
  },
  conversion: {
    belowLow: '#dcfce7',
    belowHigh: '#16a34a',
    aboveLow: '#dcfce7',
    aboveHigh: '#16a34a',
  },
  asp: {
    belowLow: '#cffafe',
    belowHigh: '#0891b2',
    aboveLow: '#cffafe',
    aboveHigh: '#0891b2',
  },
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length !== 6) return { r: 0, g: 0, b: 0 };
  const r = Number.parseInt(cleaned.slice(0, 2), 16);
  const g = Number.parseInt(cleaned.slice(2, 4), 16);
  const b = Number.parseInt(cleaned.slice(4, 6), 16);
  return { r, g, b };
}

export function interpolateColor(
  lowHex: string,
  highHex: string,
  t: number
): { r: number; g: number; b: number } {
  const clampedT = Math.min(1, Math.max(0, t));
  const low = hexToRgb(lowHex);
  const high = hexToRgb(highHex);
  return {
    r: Math.round(low.r + (high.r - low.r) * clampedT),
    g: Math.round(low.g + (high.g - low.g) * clampedT),
    b: Math.round(low.b + (high.b - low.b) * clampedT),
  };
}

const NULL_CELL = {
  // Gradients are invalid on `backgroundColor` and get ignored by the browser,
  // which leaves the previous cell color stuck after filtering. Use both props.
  backgroundColor: 'rgb(249 250 251)',
  backgroundImage:
    'repeating-linear-gradient(135deg, rgb(249 250 251), rgb(249 250 251) 6px, rgb(243 244 246) 6px, rgb(243 244 246) 12px)',
  color: 'rgb(156 163 175)',
} satisfies { backgroundColor: string; backgroundImage: string; color: string };

export function heatmapLegendStops(metricId: HeatmapMetricId): { label: string; color: string }[] {
  const palette = METRIC_HEATMAP_PALETTES[metricId];
  return [
    { label: 'Below', color: palette.belowHigh },
    { label: 'Above', color: palette.aboveHigh },
  ];
}

function textColorForT(t: number): string {
  return t > 0.55 ? '#ffffff' : '#0f172a';
}

/** Color a weekly schedule cell by multiplier (1× light → 5×+ dark), matching dayparting heatmap cells. */
export function scheduleMultiplierCellStyle(multiplier: number | null): CSSProperties {
  if (multiplier == null || !Number.isFinite(multiplier) || multiplier <= 0) {
    return { ...NULL_CELL };
  }
  const t = Math.min(1, Math.max(0, (multiplier - 1) / 4));
  const { r, g, b } = interpolateColor('#dbeafe', '#1d4ed8', t);
  return {
    backgroundColor: `rgb(${r},${g},${b})`,
    backgroundImage: 'none',
    color: textColorForT(t),
  };
}

export function heatmapCellStyle(
  metricId: HeatmapMetricId,
  value: number | null,
  range: MetricRange | null,
  thresholdHigh?: number | null
): CSSProperties {
  if (value === null || !Number.isFinite(value)) return { ...NULL_CELL };
  if (!range) {
    return {
      backgroundColor: 'rgb(239 246 255)',
      backgroundImage: 'none',
      color: 'rgb(30 58 138)',
    };
  }

  const { min, max } = range;
  const palette = METRIC_HEATMAP_PALETTES[metricId];

  // Determine below/above region first, then interpolate within that region.
  // This ensures the hue changes across the threshold (not just alpha).
  let t: number;
  let lowHex: string;
  let highHex: string;
  let reverseGradient = false;

  if (min === max) {
    // Flat range: pick palette based on threshold (if known).
    const useAbove =
      typeof thresholdHigh === 'number' && Number.isFinite(thresholdHigh) ? value >= thresholdHigh : true;
    lowHex = useAbove ? palette.aboveLow : palette.belowLow;
    highHex = useAbove ? palette.aboveHigh : palette.belowHigh;
    t = 0.5;
  } else if (typeof thresholdHigh === 'number' && Number.isFinite(thresholdHigh)) {
    const thr = thresholdHigh;
    if (thr > min && thr < max) {
      if (value <= thr) {
        lowHex = palette.belowLow;
        highHex = palette.belowHigh;
        t = Math.min(1, Math.max(0, (value - min) / (thr - min)));
        // Sales below median: darker red for lower values, lighter for values near threshold.
        reverseGradient = metricId === 'sales';
      } else {
        lowHex = palette.aboveLow;
        highHex = palette.aboveHigh;
        t = Math.min(1, Math.max(0, (value - thr) / (max - thr)));
      }
    } else if (thr >= max) {
      // Entire range is below the threshold, so stay on the below palette.
      lowHex = palette.belowLow;
      highHex = palette.belowHigh;
      t = Math.min(1, Math.max(0, (value - min) / (max - min)));
      reverseGradient = metricId === 'sales';
    } else {
      // Entire range is above the threshold, so stay on the above palette.
      lowHex = palette.aboveLow;
      highHex = palette.aboveHigh;
      t = Math.min(1, Math.max(0, (value - min) / (max - min)));
    }
  } else {
    // No threshold: full-range gradient using below->above endpoints.
    lowHex = palette.belowLow;
    highHex = palette.aboveHigh;
    t = Math.min(1, Math.max(0, (value - min) / (max - min)));
  }

  const colorT = reverseGradient ? 1 - t : t;
  const { r, g, b } = interpolateColor(lowHex, highHex, colorT);

  return {
    backgroundColor: `rgb(${r},${g},${b})`,
    backgroundImage: 'none',
    color: textColorForT(colorT),
  };
}

